import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { Mic, Square } from "lucide-react";
import { chatApi } from "./chat-api";
import {
  appendDictation, MAX_RECORDING_SECONDS, VoiceRecorder, type VoiceEnvironment
} from "./voice-recorder";

export { appendDictation, MAX_RECORDING_SECONDS };

/** A transcrição depende de chave no servidor. Uma consulta por sessão serve todos os campos. */
let availability: Promise<boolean> | undefined;
function transcriptionAvailable() {
  return availability ??= chatApi.catalog().then((catalog) => catalog.transcription).catch(() => false);
}

function browserEnvironment(): VoiceEnvironment {
  return {
    getStream: () => navigator.mediaDevices.getUserMedia({ audio: true }),
    isTypeSupported: (mimeType) => MediaRecorder.isTypeSupported(mimeType),
    createRecorder: (stream, mimeType) => new MediaRecorder(stream as MediaStream, { mimeType }) as unknown as ReturnType<VoiceEnvironment["createRecorder"]>,
    createBlob: (chunks, mimeType) => new Blob(chunks, { type: mimeType }),
    transcribe: (audio) => chatApi.transcribe(audio)
  };
}

export interface VoiceDictation {
  supported: boolean;
  recording: boolean;
  transcribing: boolean;
  seconds: number;
  toggle: () => void;
}

/**
 * Grava, transcreve e devolve o texto para revisão — o envio continua sendo do usuário.
 * `available` evita uma segunda consulta ao catálogo quando quem chama já o carregou.
 */
export function useVoiceDictation({ onTranscribed, onError, available }: {
  onTranscribed: (text: string) => void;
  onError?: (message: string) => void;
  available?: boolean;
}): VoiceDictation {
  const [supported, setSupported] = useState(available ?? false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const recorder = useRef<VoiceRecorder | undefined>(undefined);
  const handlers = useRef({ onTranscribed, onError });
  handlers.current = { onTranscribed, onError };

  useEffect(() => {
    if (available !== undefined) { setSupported(available); return; }
    let live = true;
    void transcriptionAvailable().then((value) => { if (live) setSupported(value); });
    return () => { live = false; };
  }, [available]);

  useEffect(() => {
    recorder.current = new VoiceRecorder(browserEnvironment(), {
      onRecordingChange: setRecording,
      onTranscribingChange: setTranscribing,
      onText: (text) => handlers.current.onTranscribed(text),
      onError: (error) => handlers.current.onError?.(error.message)
    });
    return () => { recorder.current?.dispose(); recorder.current = undefined; };
  }, []);

  useEffect(() => {
    if (!recording) return;
    setSeconds(0);
    const timer = setInterval(() => setSeconds((value) => {
      if (value >= MAX_RECORDING_SECONDS - 1) recorder.current?.stop();
      return value + 1;
    }), 1000);
    return () => clearInterval(timer);
  }, [recording]);

  const toggle = useCallback(() => {
    if (recording) recorder.current?.stop();
    else void recorder.current?.start();
  }, [recording]);

  return { supported, recording, transcribing, seconds, toggle };
}

/** Botão idêntico ao do compositor do chat: mesmo ícone, mesmo tamanho, mesmos rótulos. */
export function VoiceButton({ dictation, disabled, label, withStatusTitle, onBeforeToggle }: {
  dictation: VoiceDictation;
  disabled?: boolean;
  label?: string;
  withStatusTitle?: boolean;
  onBeforeToggle?: () => void;
}) {
  if (!dictation.supported) return null;
  const title = withStatusTitle
    ? dictation.recording ? `Gravando · ${dictation.seconds}s / ${MAX_RECORDING_SECONDS}s`
      : dictation.transcribing ? "Transcrevendo…" : label ?? "Ditar por voz"
    : undefined;
  return <button
    type="button"
    className={`icon-button ${dictation.recording ? "recording" : ""}`}
    aria-label={dictation.recording ? "Parar gravação e transcrever" : label ?? "Gravar mensagem de áudio"}
    title={title}
    disabled={disabled || dictation.transcribing}
    onClick={() => { onBeforeToggle?.(); dictation.toggle(); }}
  >{dictation.recording ? <Square size={17} /> : <Mic size={18} />}</button>;
}

/** Atalho para os campos que só precisam acrescentar o ditado ao próprio texto. */
export function DictationButton({ value, onChange, maxChars, disabled, onError, label, focusRef }: {
  value: string;
  onChange: (next: string) => void;
  maxChars: number;
  disabled?: boolean;
  onError?: (message: string) => void;
  label?: string;
  focusRef?: RefObject<HTMLTextAreaElement | HTMLInputElement | null>;
}) {
  const latest = useRef(value);
  latest.current = value;
  const dictation = useVoiceDictation({
    onError,
    onTranscribed: (text) => {
      onChange(appendDictation(latest.current, text, maxChars));
      focusRef?.current?.focus();
    }
  });
  return <VoiceButton dictation={dictation} disabled={disabled} label={label} withStatusTitle />;
}
